import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { HeartHandshake } from "lucide-react";

/**
 * FR-TEN-07: the public website, rendered from CMS pages.
 *
 * Deliberately does NOT use lib/api: that helper attaches the access token and
 * hard-redirects to /login on a 401. These pages are for the open internet,
 * where there is no session and a redirect to a login screen would be wrong.
 */

interface PublicPageData {
  slug: string;
  title: string;
  body: string;
  excerpt: string | null;
  heroImageUrl: string | null;
  seoTitle: string | null;
  seoDescription: string | null;
  publishedAt: string | null;
}

const BASE = import.meta.env.VITE_API_URL ?? "";

/**
 * Set the document title, meta description, canonical URL, and Open Graph
 * tags from the page's SEO fields — the reason those fields exist
 * (FR-TEN-07). The Open Graph tags are what a link to this page looks like
 * when someone pastes it into a chat, which for a marketing page is most of
 * the traffic.
 *
 * Everything is restored on unmount, so navigating from a CMS page back into
 * the app does not leave the app wearing the page's metadata.
 */
function useSeo(page: PublicPageData | null) {
  useEffect(() => {
    if (!page) return;

    const title = page.seoTitle ?? page.title;
    const description = page.seoDescription ?? page.excerpt ?? "";
    const previousTitle = document.title;
    document.title = title;

    /** Upsert a head element, remembering enough to put it back. */
    const restores: (() => void)[] = [];
    const setTag = (selector: string, create: () => HTMLElement, attr: string, value: string) => {
      if (!value) return;
      const existing = document.head.querySelector<HTMLElement>(selector);
      const el = existing ?? create();
      const previous = existing?.getAttribute(attr) ?? null;
      if (!existing) document.head.appendChild(el);
      el.setAttribute(attr, value);
      restores.push(() => {
        if (!existing) el.remove();
        else if (previous !== null) el.setAttribute(attr, previous);
      });
    };

    const meta = (name: string) => () => {
      const el = document.createElement("meta");
      el.setAttribute("name", name);
      return el;
    };
    const og = (property: string) => () => {
      const el = document.createElement("meta");
      el.setAttribute("property", property);
      return el;
    };
    const link = (rel: string) => () => {
      const el = document.createElement("link");
      el.setAttribute("rel", rel);
      return el;
    };

    setTag('meta[name="description"]', meta("description"), "content", description);
    setTag('meta[property="og:title"]', og("og:title"), "content", title);
    setTag('meta[property="og:description"]', og("og:description"), "content", description);
    setTag('meta[property="og:type"]', og("og:type"), "content", "article");
    setTag('meta[property="og:url"]', og("og:url"), "content", window.location.href);
    setTag('meta[name="twitter:card"]', meta("twitter:card"), "content",
      page.heroImageUrl ? "summary_large_image" : "summary");
    if (page.heroImageUrl) {
      setTag('meta[property="og:image"]', og("og:image"), "content", page.heroImageUrl);
    }
    setTag('link[rel="canonical"]', link("canonical"), "href", window.location.href);

    return () => {
      document.title = previousTitle;
      for (const restore of restores) restore();
    };
  }, [page]);
}

function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-canvas">
      <header className="border-b border-edge bg-surface">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-4">
          <Link to="/site" className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-teal-700">
              <HeartHandshake className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-ink">NDIS Platform</span>
          </Link>
          <Link to="/login" className="text-sm text-mut hover:text-body">Sign in</Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-6 py-10">{children}</main>
    </div>
  );
}

export function PublicIndex() {
  const [pages, setPages] = useState<PublicPageData[] | null>(null);

  useEffect(() => {
    fetch(`${BASE}/v1/pages`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setPages)
      .catch(() => setPages([]));
  }, []);

  return (
    <PublicShell>
      <h1 className="text-2xl font-semibold text-ink">Pages</h1>
      {pages === null && <p className="mt-4 text-sm text-mut">Loading…</p>}
      {pages?.length === 0 && <p className="mt-4 text-sm text-mut">Nothing published yet.</p>}
      <ul className="mt-6 space-y-3">
        {(pages ?? []).map((p) => (
          <li key={p.slug} className="card p-4">
            <Link to={`/site/${p.slug}`} className="font-medium text-ink hover:underline">{p.title}</Link>
            {p.excerpt && <p className="mt-1 text-sm text-mut">{p.excerpt}</p>}
          </li>
        ))}
      </ul>
    </PublicShell>
  );
}

export default function PublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<PublicPageData | null>(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setPage(null);
    setMissing(false);
    fetch(`${BASE}/v1/pages/${encodeURIComponent(slug)}`)
      .then((r) => {
        // The API 404s unpublished pages, so a draft is indistinguishable from
        // a page that never existed. That is deliberate — see 0008_cms.
        if (!r.ok) { setMissing(true); return null; }
        return r.json();
      })
      .then((data) => { if (data) setPage(data); })
      .catch(() => setMissing(true));
  }, [slug]);

  useSeo(page);

  if (missing) {
    return (
      <PublicShell>
        <h1 className="text-2xl font-semibold text-ink">Page not found</h1>
        <p className="mt-2 text-sm text-mut">
          That page does not exist, or is not published.
        </p>
        <Link to="/site/pages" className="mt-4 inline-block text-sm text-teal-700 hover:underline">
          Back to all pages
        </Link>
      </PublicShell>
    );
  }

  if (!page) return <PublicShell><p className="text-sm text-mut">Loading…</p></PublicShell>;

  return (
    <PublicShell>
      <article>
        {page.heroImageUrl && (
          <img src={page.heroImageUrl} alt="" className="mb-6 w-full rounded-xl object-cover" />
        )}
        <h1 className="text-3xl font-semibold text-ink">{page.title}</h1>
        {page.publishedAt && (
          <p className="mt-1 text-xs text-mut">
            Published {new Date(page.publishedAt).toLocaleDateString("en-AU", {
              day: "numeric", month: "long", year: "numeric",
            })}
          </p>
        )}
        {/* Body is plain text authored by a platform admin. Rendered as text
            rather than HTML — CMS content is not a reason to open an XSS hole. */}
        <div className="mt-6 space-y-4 text-body">
          {page.body.split(/\n{2,}/).map((para, i) => (
            <p key={i} className="whitespace-pre-wrap leading-relaxed">{para}</p>
          ))}
        </div>
      </article>
    </PublicShell>
  );
}
